/* Feb-2026 · Crown & Rose — Author Publishing Agreement sign card.
 *
 * Rendered on the Business Dashboard next to the WIPO "My Contracts"
 * card. Handles the three states of the platform-wide agreement:
 *   • not signed          → "Sign Agreement" flow (typed name + I agree)
 *   • pending_admin       → status pill + note that admin is reviewing
 *   • executed            → PDF download + full-executed date
 *   • rejected            → red banner with admin's rejection reason,
 *                           and the sign form reopens for a fresh signature
 *
 * All copy is verbatim from the canonical text served by
 * `GET /api/author-agreements/current` — we never hard-code the terms
 * on the frontend so the SHA-256 tamper seal survives a copy sweep.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ScrollText, PenTool, Loader2, Download, Check, X,
  CircleAlert, CircleCheck, Hourglass,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const STATUS = {
  pending_admin: {
    label: 'Awaiting Company countersignature',
    tone:  'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
    Icon:  Hourglass,
  },
  executed: {
    label: 'Fully executed',
    tone:  'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
    Icon:  CircleCheck,
  },
  rejected: {
    label: 'Rejected — please re-sign',
    tone:  'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40',
    Icon:  CircleAlert,
  },
};

export default function AuthorAgreementCard() {
  const [me, setMe]         = useState(null);       // { agreement, needs_signature, ... }
  const [tpl, setTpl]       = useState(null);       // { markdown, terms, version, ... }
  const [loading, setLoading]       = useState(true);
  const [signing, setSigning]       = useState(false);
  const [signature, setSignature]   = useState('');
  const [agreed, setAgreed]         = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [mineR, tplR] = await Promise.all([
        http.get('/author-agreements/me'),
        http.get('/author-agreements/current'),
      ]);
      setMe(mineR.data);
      setTpl(tplR.data);
    } catch (_e) {
      toast.error('Could not load your Publishing Agreement.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!agreed)          return toast.error('Please tick "I agree" first.');
    if (signature.trim().length < 2) return toast.error('Type your full legal name to sign.');
    setSigning(true);
    try {
      await http.post('/author-agreements/sign', {
        signature: signature.trim(), agreed: true, version: tpl.version,
      });
      toast.success('Signed — the admin has been notified for countersignature.');
      setSignature(''); setAgreed(false);
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Signing failed.');
    } finally { setSigning(false); }
  };

  const agreement = me?.agreement;
  const needsSig  = !!me?.needs_signature || (agreement && tpl && agreement.contract_version !== tpl.version);
  const statusMeta = agreement ? STATUS[agreement.status] : null;

  const royaltyActive = tpl?.terms?.royalty_active;
  const royaltyLapsed = tpl?.terms?.royalty_lapsed;

  if (loading && !me) {
    return (
      <div className="rounded-lg border border-border bg-card p-4" data-testid="author-agreement-card-loading">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="author-agreement-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-[13px] font-semibold tracking-tight">
          <ScrollText className="h-4 w-4 text-primary" />
          Publishing Agreement
          <span className="rounded-sm border border-border bg-panel-header/50 px-1.5 py-[1px] text-[10px] font-normal text-muted-foreground">
            {tpl?.version || 'v?'}
          </span>
        </div>
        {statusMeta && (
          <div
            data-testid="author-agreement-status-pill"
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-[2px] text-[10.5px] font-semibold ${statusMeta.tone}`}
          >
            <statusMeta.Icon className="h-3 w-3" /> {statusMeta.label}
          </div>
        )}
      </div>

      {/* iter-resign-banner · Feb-2026 — Version-drift banner. Shows
       * whenever the author's latest agreement is on an older version
       * than the current canonical text (v1.0 → v1.1 rollout was the
       * seed case: added the §2.6 Amazon KDP / Google Books addendum). */}
      {agreement && tpl && agreement.contract_version !== tpl.version && (
        <div
          data-testid="author-agreement-resign-banner"
          className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-800 dark:text-amber-200"
        >
          <ScrollText className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300" />
          <span>
            A newer version <strong>{tpl.version}</strong> of the Publishing
            Agreement is available (your current signature is on {agreement.contract_version}).
            The new §2.6 addendum clarifies the 90/10 split on Amazon KDP and Google Play Books (90% to you after the retailer's platform fee) versus the 85/15 split on the Crown &amp; Rose direct store.
            when we publish through the Crown &amp; Rose account.
            {agreement.status === 'pending_admin' && (
              <> Your pending signature can\u2019t be countersigned until you re-sign the current version.</>
            )}
          </span>
          <button
            type="button"
            onClick={() => { setDialogOpen(true); setSignature(''); setAgreed(false); }}
            className="ml-auto inline-flex items-center rounded-sm border border-amber-600/60 bg-amber-500/20 px-2.5 py-1 text-[11.5px] font-semibold text-amber-900 hover:bg-amber-500/30 dark:text-amber-100"
          >
            Re-sign now
          </button>
        </div>
      )}

      {/* Terms summary — same numbers the canonical text stipulates. */}
      {royaltyActive && (
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2" data-testid="author-agreement-terms-active">
            <div className="text-[10.5px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              While subscribed
            </div>
            <div className="text-[13px] font-semibold">
              {royaltyActive.author_pct}% author / {royaltyActive.company_pct}% company
            </div>
            <div className="text-[11px] text-muted-foreground">{royaltyActive.condition}</div>
          </div>
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2" data-testid="author-agreement-terms-lapsed">
            <div className="text-[10.5px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              If you leave the platform
            </div>
            <div className="text-[13px] font-semibold">
              {royaltyLapsed.author_pct}% author / {royaltyLapsed.company_pct}% company
            </div>
            <div className="text-[11px] text-muted-foreground">{royaltyLapsed.condition}</div>
          </div>
        </div>
      )}

      {/* Rejection banner (if applicable) */}
      {agreement?.status === 'rejected' && (
        <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 p-2 text-[12px] text-red-700 dark:text-red-300" data-testid="author-agreement-rejection">
          <strong>Rejected by admin.</strong> {agreement.rejection_reason || '—'}
        </div>
      )}

      {/* Executed — show download + counter-signature block */}
      {agreement?.status === 'executed' && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <a
            href={`${API}/author-agreements/me/pdf`}
            target="_blank" rel="noreferrer"
            data-testid="author-agreement-download-pdf"
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-[12px] font-semibold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
          >
            <Download className="h-3.5 w-3.5" /> Download signed PDF
          </a>
          <div className="text-[11px] text-muted-foreground">
            Signed <strong>{agreement.author_signed_at?.slice(0, 10)}</strong>
            {' · '}Countersigned <strong>{agreement.admin_signed_at?.slice(0, 10)}</strong>
            {agreement.admin_display && (<><br />{agreement.admin_display}</>)}
          </div>
        </div>
      )}

      {/* Pending-admin — waiting note */}
      {agreement?.status === 'pending_admin' && (
        <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-[12px]" data-testid="author-agreement-pending-note">
          Signed <strong>{agreement.author_signed_at?.slice(0, 10)}</strong> — an
          administrator will review and countersign within one business day. The
          fully executed PDF will appear here once it lands.
        </div>
      )}

      {/* Sign form (unsigned OR rejected) */}
      {needsSig && (
        <>
          {/* Dialog with full agreement text */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="max-w-2xl gap-0 p-0 overflow-hidden" data-testid="author-agreement-dialog">
              <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
                <DialogTitle className="flex items-center gap-2 text-[14px]">
                  <ScrollText className="h-4 w-4 text-primary" />
                  Publishing Agreement
                  <span className="text-[11px] font-normal text-muted-foreground ml-1">{tpl?.version || ''}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto max-h-[60vh] px-5 py-4">
                <pre
                  data-testid="author-agreement-full-text"
                  className="text-[12px] leading-relaxed whitespace-pre-wrap font-serif text-foreground"
                >
                  {tpl?.markdown || ''}
                </pre>
              </div>
              <div className="border-t border-border px-5 py-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90"
                >
                  <Check className="h-3.5 w-3.5" /> Done reading
                </button>
              </div>
            </DialogContent>
          </Dialog>

          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            data-testid="author-agreement-open-btn"
            className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-primary hover:underline"
          >
            <ScrollText className="h-3.5 w-3.5" /> Read the full agreement
          </button>
          <div className="mt-3 grid gap-2">
            <label className="text-[11px]">
              <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                Type your full legal name
              </span>
              <input
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Jane Q. Author"
                data-testid="author-agreement-typed-name"
                className="mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[13px] font-serif italic"
              />
            </label>
            <label className="flex items-start gap-2 text-[12px]">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="author-agreement-agree-checkbox"
                className="mt-[3px]"
              />
              <span>
                I have read the Agreement in full and I accept the
                hold-harmless, no-warranty, and indemnity terms. I understand
                Crown & Rose LLC makes no promise of sales and that all
                foreseen or unforeseen damages arising from platform use are
                accepted by me.
              </span>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={submit}
                disabled={signing || !agreed || signature.trim().length < 2}
                data-testid="author-agreement-sign-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground shadow hover:opacity-90 disabled:opacity-50"
              >
                {signing
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Signing…</>
                  : <><PenTool className="h-3.5 w-3.5" /> Sign Agreement</>}
              </button>
              <button
                type="button"
                onClick={() => { setSignature(''); setAgreed(false); }}
                className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2.5 py-1.5 text-[11.5px] text-muted-foreground hover:bg-secondary"
                data-testid="author-agreement-clear-btn"
              >
                <X className="h-3 w-3" /> Clear
              </button>
              <div className="ml-auto text-[10.5px] text-muted-foreground">
                <Check className="mr-1 inline h-3 w-3" />
                Electronically signed under the E-SIGN Act.
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
